import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Repeat, ClipboardList, History, Loader2, AlertTriangle } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { OrderHistory } from "@/components/orders/OrderHistory";
import { OrderStopsSection } from "@/components/orders/OrderStopsSection";
import { toast } from "sonner";
import {
  Order,
  OrderStatus,
  Customer,
  Tractor,
  Driver,
  Location,
  ORDER_STATUS_LABELS,
} from "@/types/tms";
import {
  OrderFormData,
  LegData,
  makeEmptyLeg,
  seedFutureWindow,
  addMinutesLocal,
  DEFAULT_WINDOW_MINUTES,
} from "@/components/orders/orderFormTypes";
import { MIN_LEG_GAP_MINUTES } from "@/hooks/useOrderFormLegLogic";
import { validateLegs } from "@/hooks/useOrderFormLegLogic";
import { isOrderFormValid } from "@/lib/order-form-validation";
import { SubmitWithReasons } from "@/components/orders/SubmitWithReasons";
import { isPerLegAssets, isPerLegTripType } from "@/lib/order-form-assets";
import { useTrailerTypes } from "@/hooks/useTrailerTypes";
import { useTenant } from "@/hooks/useTenant";
import { TrailerIdField } from "@/components/orders/TrailerIdField";
import { NeedsServiceBlockedDialog } from "@/components/orders/NeedsServiceBlockedDialog";
import { TractorCombobox } from "@/components/orders/TractorCombobox";
import { DriverCombobox } from "@/components/orders/DriverCombobox";
import { formLocalToUtc, getNowInPlanningTz } from "@/lib/timezone";
import { computeDurationWarnings, formatDurationWarning } from "@/lib/order-duration-warning";
import { useTractorConflictIdsByWindows } from "@/hooks/useTractorConflictIds";
import {
  useTractorOosStatusMap,
  computeOosBlockedIdsForWindow,
} from "@/hooks/useTractorOosStatusMap";
import { useTruckEligibilityMap } from "@/hooks/useTruckEligibility";
import {
  computeEligibilityReasons,
  validateTractorEligibility,
} from "@/lib/truck-eligibility";
import { buildEligibilityChecks } from "@/components/orders/tractorEligibilityChecks";
import {
  useResolvedCheckedInTrailer,
  useSyncInheritedTrailerType,
} from "@/components/orders/trailerTypeFieldLogic";
import { CheckedInTrailerTypeOverrideDialog } from "@/components/orders/CheckedInTrailerTypeOverrideDialog";
import { tripTypeOptionsForTenant } from "@/lib/feature-flags";
import { FormSection } from "@/components/orders/form/FormSection";
import { AdvancedFieldsToggle } from "@/components/orders/form/AdvancedFieldsToggle";
import { EquipmentAdvancedFields } from "@/components/orders/form/EquipmentAdvancedFields";
import { ShipmentFields } from "@/components/orders/form/ShipmentFields";
import { validateAdvancedFields } from "@/components/orders/form/advancedFields";
import {
  buildOosAssignmentChecks,
  validateTractorOosAssignments,
} from "@/lib/tractor-oos-preflight";
import { PendingBolCheckbox } from "@/components/orders/form/PendingBolCheckbox";

const STATUSES: OrderStatus[] = ["booked", "confirmed", "canceled"];
const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface OrderFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  editingOrder: Order | null;
  formData: OrderFormData;
  setFormData: React.Dispatch<React.SetStateAction<OrderFormData>>;
  onSubmit: (e: React.FormEvent) => void;
  onReset: () => void;
  isSubmitting: boolean;
  customers: Customer[] | undefined;
  tractors: Tractor[] | undefined;
  drivers: Driver[] | undefined;
  locations: Location[] | undefined;
  /** When true, the form is dedicated to creating a recurring template:
   *  forces `is_recurring=true`, hides the header Recurring checkbox, and
   *  skips the past-time floor (occurrences are scheduled by cadence). */
  forceRecurring?: boolean;
  /** When set, pre-fills and disables the Billing Party select. */
  lockBillingPartyId?: string;
  /** Optional override for the dialog title (create flow only). */
  titleOverride?: string;
  /** When true, the dialog is editing a recurring template (not an order):
   *  - hides assignment-only fields (tractor / operators / tender)
   *  - hides the recurring header checkbox; forces `is_recurring=true`
   *  - skips the seed-future-window effect (caller pre-seeds from template)
   *  - shows the recurrence-details panel even without `editingOrder`
   *  - submit label becomes "Update Template"
   *  The caller is responsible for translating the submitted `OrderFormData`
   *  back into a `recurring_order_templates` patch. */
  templateEditMode?: boolean;
}

export function OrderFormDialog({
  isOpen,
  onOpenChange,
  editingOrder,
  formData,
  setFormData,
  onSubmit,
  onReset,
  isSubmitting,
  customers,
  tractors,
  drivers,
  locations,
  forceRecurring = false,
  lockBillingPartyId,
  titleOverride,
  templateEditMode = false,
}: OrderFormDialogProps) {
  const [activeTab, setActiveTab] = useState<"details" | "history">("details");
  const durationWarnings = useMemo(
    () =>
      computeDurationWarnings(formData, (local) => formLocalToUtc(local)).filter((w) => w.legNumber == null),
    [formData],
  );
  const { data: trailerTypes } = useTrailerTypes();
  const { activeTenant, tenantId } = useTenant();
  const showAdvanced = formData.show_advanced_fields === true;
  const queryClient = useQueryClient();
  const [needsServiceBlock, setNeedsServiceBlock] = useState<{
    trailerId: string;
    reason: string | null;
  } | null>(null);
  const tripTypeOptions = tripTypeOptionsForTenant(activeTenant);
  const [recurrenceEndType, setRecurrenceEndType] = useState<"never" | "ends_on">(
    formData.recurrence_end_date ? "ends_on" : "never"
  );

  // Re-seed time fields with the next future LA-local window each time the
  // create dialog opens. Without this, the static defaults baked into
  // `initialFormData` immediately fall into the past after ~08:00 LA, which
  // disables the submit button until the user manually bumps every field.
  // We only run this for the create flow (no editingOrder) and only when the
  // form hasn't been touched (legs empty + initial single-leg shape).
  const seededRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      seededRef.current = false;
      return;
    }
    if (editingOrder) return;
    // Template edit pre-seeds from the template row; never overwrite it.
    if (templateEditMode) return;
    if (seededRef.current) return;
    seededRef.current = true;
    const w = seedFutureWindow();
    setFormData((prev) => ({
      ...prev,
      ...w,
      ...(forceRecurring ? { is_recurring: true } : {}),
      ...(lockBillingPartyId ? { billing_party_id: lockBillingPartyId } : {}),
    }));
  }, [isOpen, editingOrder, setFormData, forceRecurring, lockBillingPartyId, templateEditMode]);

  // Resolve trailer ID against the checked-in roster so we can drive the Trailer Type field.
  const { matched, matchedTrailerRowId, inheritedTypeId, inheritedTypeLabel } = useResolvedCheckedInTrailer(
    formData.trailer_id,
  );
  // Trailer type is purely informational at create time. Truck/trailer/type
  // are no longer required for creating a Booked order — they're enforced when
  // the order is promoted to Confirmed via the edit flow.
  const trailerTypeRequired = false;

  // Override flow: lets the user change the trailer type even when a
  // checked-in trailer supplied it. The new value is staged here and the
  // matched `trailers` row is updated on submit.
  const [pendingTypeChange, setPendingTypeChange] = useState<string | null>(null);
  const [trailerTypeOverride, setTrailerTypeOverride] = useState<{
    trailerRowId: string;
    newTypeId: string;
  } | null>(null);

  // Drop any pending override when the matched trailer changes (different
  // trailer_id, or unmatched) so we never write to the wrong row.
  useEffect(() => {
    setTrailerTypeOverride((prev) => {
      if (!prev) return prev;
      if (!matchedTrailerRowId || prev.trailerRowId !== matchedTrailerRowId) return null;
      return prev;
    });
    setPendingTypeChange(null);
  }, [matchedTrailerRowId]);

  useSyncInheritedTrailerType(
    inheritedTypeId,
    matched,
    formData.trailer_type_id,
    useCallback((id: string) => setFormData((prev) => ({ ...prev, trailer_type_id: id })), [setFormData]),
    !!trailerTypeOverride,
  );

  const handleTrailerTypeChange = (value: string) => {
    const next = value === "none" ? "" : value;
    if (matched && matchedTrailerRowId && next !== (inheritedTypeId ?? "")) {
      setPendingTypeChange(next);
      return;
    }
    setFormData((prev) => ({ ...prev, trailer_type_id: next }));
  };

  const confirmTrailerTypeOverride = () => {
    if (pendingTypeChange === null || !matchedTrailerRowId) {
      setPendingTypeChange(null);
      return;
    }
    const newTypeId = pendingTypeChange;
    setFormData((prev) => ({ ...prev, trailer_type_id: newTypeId }));
    setTrailerTypeOverride({ trailerRowId: matchedTrailerRowId, newTypeId });
    setPendingTypeChange(null);
  };

  const cancelTrailerTypeOverride = () => setPendingTypeChange(null);

  // Reset override state when the dialog closes.
  useEffect(() => {
    if (!isOpen) {
      setPendingTypeChange(null);
      setTrailerTypeOverride(null);
    }
  }, [isOpen]);

  const { data: recurringTemplate } = useQuery({
    queryKey: ["recurring-template", editingOrder?.recurring_order_template_id],
    queryFn: async () => {
      if (!editingOrder?.recurring_order_template_id) return null;
      const { data } = await supabase
        .from("recurring_order_templates")
        .select("unique_id")
        .eq("id", editingOrder.recurring_order_template_id)
        .single();
      return data;
    },
    enabled: !!editingOrder?.recurring_order_template_id,
  });

  // Compute the form's effective time window (Central → UTC) so we can
  // ask the server which tractors already have a conflicting assignment.
  const toUtcIso = (local: string): string | null => formLocalToUtc(local);
  // Past-time enforcement now lives in the shared `isOrderFormValid` helper.
  // Floor for date/time pickers — only applied when creating a new,
  // non-recurring order. Matches the form's submit contract (Central).
  const nowFloor = !editingOrder && !formData.is_recurring && !forceRecurring && !templateEditMode
    ? getNowInPlanningTz()
    : undefined;
  /** One conflict window per leg (multi-leg) or a single order-level window. */
  const legConflictWindows = (() => {
    if (formData.order_type === "multi-leg") {
      return formData.legs.map((leg) => ({
        start: toUtcIso(leg.ready_time),
        end: toUtcIso(leg.requested_delivery_time_end || leg.requested_delivery_time),
      }));
    }
    return [
      {
        start: toUtcIso(formData.ready_time),
        end: toUtcIso(
          formData.requested_delivery_time_end || formData.requested_delivery_time,
        ),
      },
    ];
  })();
  const conflictWindow = (() => {
    const starts = legConflictWindows.map((w) => w.start).filter(Boolean) as string[];
    const ends = legConflictWindows.map((w) => w.end).filter(Boolean) as string[];
    if (!starts.length || !ends.length) return null;
    return {
      start: starts.reduce((a, b) => (a < b ? a : b)),
      end: ends.reduce((a, b) => (a > b ? a : b)),
    };
  })();

  // Per-leg booked lookup: a conflict on one leg must not block a truck on a
  // non-overlapping sibling leg.
  const { perWindow: conflictIdsByLeg, union: conflictingTractorIds } =
    useTractorConflictIdsByWindows({
      tenantId,
      windows: legConflictWindows,
      excludeOrderId: editingOrder?.id,
      excludeLegOrderId: editingOrder?.id,
    });


  // OOS awareness.
  const { statusById: oosStatusById, recordsById: oosRecordsById } =
    useTractorOosStatusMap();
  // OOS blocking: for multi-leg + "use same tractor", union per-leg helper
  // results so partial windows still block (matches per-leg selector).
  const oosBlockedByLeg = legConflictWindows.map((w) =>
    computeOosBlockedIdsForWindow(oosRecordsById, w.start, w.end),
  );
  const oosBlockedForWindow = new Set<string>();
  oosBlockedByLeg.forEach((set) => set.forEach((id) => oosBlockedForWindow.add(id)));
  const mergedDisabledTractorIds = new Set<string>([
    ...(conflictingTractorIds ?? []),
    ...oosBlockedForWindow,
  ]);
  /** Per-leg disabled ids so a conflict on one leg doesn't block the others. */
  const disabledTractorIdsByLeg = legConflictWindows.map(
    (_w, index) =>
      new Set<string>([
        ...(conflictIdsByLeg[index] ?? []),
        ...(oosBlockedByLeg[index] ?? []),
      ]),
  );


  // Truck eligibility. Hard block: a tractor whose latest AAM event doesn't
  // allow the selected trip type can't be picked.
  const { eligibilityByTractor, gateState: eligibilityGateState } =
    useTruckEligibilityMap();
  const eligibilityReasonsById = computeEligibilityReasons(
    eligibilityByTractor,
    formData.trip_type,
    (tractors ?? []).map((t) => t.id),
    eligibilityGateState,
  );

  const handleOrderTypeChange = (value: "single-leg" | "multi-leg") => {
    if (value === "multi-leg") {
      const leg0: LegData = {
        ship_from_location_id: formData.ship_from_location_id,
        ship_from_street_address: formData.ship_from_street_address,
        ship_from_city: formData.ship_from_city,
        ship_from_province_state: formData.ship_from_province_state,
        ship_from_postal_zip_code: formData.ship_from_postal_zip_code,
        ship_from_country: formData.ship_from_country,
        ship_to_location_id: formData.ship_to_location_id,
        ship_to_street_address: formData.ship_to_street_address,
        ship_to_city: formData.ship_to_city,
        ship_to_province_state: formData.ship_to_province_state,
        ship_to_postal_zip_code: formData.ship_to_postal_zip_code,
        ship_to_country: formData.ship_to_country,
        ready_time: formData.ready_time,
        requested_delivery_time: formData.requested_delivery_time,
        ready_time_end: formData.ready_time_end,
        requested_delivery_time_end: formData.requested_delivery_time_end,
      };
      const leg1: LegData = {
        ...makeEmptyLeg(),
        ship_from_location_id: formData.ship_to_location_id,
        ship_from_street_address: formData.ship_to_street_address,
        ship_from_city: formData.ship_to_city,
        ship_from_province_state: formData.ship_to_province_state,
        ship_from_postal_zip_code: formData.ship_to_postal_zip_code,
        ship_from_country: formData.ship_to_country,
      };
      // Leg 2 starts an hour after leg 1 delivers — never on leg 1's own window.
      const ready1 = addMinutesLocal(leg0.requested_delivery_time, MIN_LEG_GAP_MINUTES);
      leg1.ready_time = ready1;
      leg1.ready_time_end = addMinutesLocal(ready1, DEFAULT_WINDOW_MINUTES);
      leg1.requested_delivery_time = addMinutesLocal(ready1, MIN_LEG_GAP_MINUTES);
      leg1.requested_delivery_time_end = addMinutesLocal(
        ready1,
        MIN_LEG_GAP_MINUTES + DEFAULT_WINDOW_MINUTES,
      );
      setFormData((prev) => ({ ...prev, order_type: "multi-leg", legs: [leg0, leg1] }));
    } else {
      const leg0 = formData.legs[0];
      if (leg0) {
        setFormData((prev) => ({
          ...prev,
          order_type: "single-leg",
          legs: [],
          ship_from_location_id: leg0.ship_from_location_id,
          ship_from_street_address: leg0.ship_from_street_address,
          ship_from_city: leg0.ship_from_city,
          ship_from_province_state: leg0.ship_from_province_state,
          ship_from_postal_zip_code: leg0.ship_from_postal_zip_code,
          ship_from_country: leg0.ship_from_country,
          ship_to_location_id: leg0.ship_to_location_id,
          ship_to_street_address: leg0.ship_to_street_address,
          ship_to_city: leg0.ship_to_city,
          ship_to_province_state: leg0.ship_to_province_state,
          ship_to_postal_zip_code: leg0.ship_to_postal_zip_code,
          ship_to_country: leg0.ship_to_country,
          ready_time: leg0.ready_time,
          requested_delivery_time: leg0.requested_delivery_time,
        }));
      } else {
        setFormData((prev) => ({ ...prev, order_type: "single-leg", legs: [] }));
      }
    }
  };

  const formValidation = isOrderFormValid(formData, { isEditing: !!editingOrder });
  const isFormValid = formValidation.valid;

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.order_type === "multi-leg" && formData.legs.length < 2) {
      toast.error("Multi-leg orders require at least 2 stops");
      return;
    }
    // Per-leg HOS / chronology / asset-conflict checks (Phase 4).
    const legErrors = validateLegs(formData);
    if (legErrors.length > 0) {
      toast.error(legErrors[0]);
      return;
    }
    // Advanced ("Order API parity") fields — only enforced while visible.
    const advancedErrors = validateAdvancedFields(formData);
    if (advancedErrors.length > 0) {
      toast.error(advancedErrors[0]);
      return;
    }
    // Truck eligibility hard block — mirrors the picker gating so a selection
    // made before a trip-type change can't be saved.
    const eligibilityError = validateTractorEligibility(
      buildEligibilityChecks(formData, tractors),
      eligibilityByTractor,
      eligibilityGateState,
    );
    if (eligibilityError) {
      toast.error(eligibilityError);
      return;
    }
    // Out-of-service hard block — a truck can go OOS after it was picked
    // (form left open, or AAM sends a service event mid-edit), so re-run the
    // picker's own rule against the same per-leg windows at save time.
    const oosError = validateTractorOosAssignments(
      buildOosAssignmentChecks(formData, tractors),
      oosBlockedByLeg,
    );
    if (oosError) {
      toast.error(oosError);
      return;
    }
    // NOTE: there is no document-confirm gate here. This dialog only ever
    // creates orders in `booked` (the Status field renders only for
    // `editingOrder`, and every caller passes `editingOrder={null}`), so a
    // create-as-confirmed gate was unreachable. Confirmation is gated on the
    // Order Detail button + Edit Order, and fails closed in
    // `useOrderMutations` if a confirmed create ever appears.
    // Safety net: when a checked-in trailer supplies a type AND the user has
    // not explicitly overridden it, make sure the payload reflects the
    // inherited value even if the sync effect hasn't flushed yet.
    if (
      !trailerTypeOverride &&
      matched &&
      inheritedTypeId &&
      formData.trailer_type_id !== inheritedTypeId
    ) {
      setFormData((prev) => ({ ...prev, trailer_type_id: inheritedTypeId }));
    }
    // Propagate a confirmed override to the matched trailers row before the
    // order itself is created. If this write fails, abort — we don't want the
    // order to be created with a type that diverges from the check-in record.
    if (trailerTypeOverride && tenantId) {
      const { error } = await supabase
        .from("trailers")
        .update({ trailer_type_id: trailerTypeOverride.newTypeId })
        .eq("id", trailerTypeOverride.trailerRowId)
        .eq("tenant_id", tenantId);
      if (error) {
        toast.error(`Failed to update check-in record: ${error.message}`);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["checked-in-trailers", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["trailers"] });
      setTrailerTypeOverride(null);
    }
    // Orders are created in Booked state with no EAPI sync. Truck/trailer/type
    // are validated at confirmation time in the edit flow.
    onSubmit(e);
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => { onOpenChange(open); if (!open) onReset(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <div className="px-6 pt-6 pb-2">
          <DialogHeader>
            <div className="flex items-center justify-between gap-4">
              <DialogTitle>
                {editingOrder ? (
                  <>
                    Edit Order {editingOrder.unique_id}
                    {recurringTemplate?.unique_id && (
                      <span className="text-muted-foreground font-normal"> ({recurringTemplate.unique_id})</span>
                    )}
                  </>
                ) : (
                  titleOverride ??
                  (templateEditMode
                    ? "Edit Recurring Order Template"
                    : forceRecurring || formData.is_recurring
                      ? "New Recurring Order Template"
                      : "New Order")
                )}
              </DialogTitle>
              <div className="ml-auto flex items-center gap-4">
              <AdvancedFieldsToggle
                id="create-show-advanced-fields"
                checked={formData.show_advanced_fields === true}
                onCheckedChange={(next) =>
                  setFormData((prev) => ({ ...prev, show_advanced_fields: next }))
                }
              />
              {!editingOrder && !forceRecurring && !templateEditMode && (
                <label
                  htmlFor="is_recurring"
                  className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none mr-6"
                >
                  <Checkbox
                    id="is_recurring"
                    checked={formData.is_recurring}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, is_recurring: checked === true }))
                    }
                  />
                  <Repeat className="h-4 w-4" />
                  Recurring Order
                </label>
              )}
              </div>
            </div>
          </DialogHeader>
        </div>

        {editingOrder && (
          <div className="flex items-center gap-6 border-b px-6">
            {([
              { value: "details" as const, label: "Details", icon: ClipboardList },
              { value: "history" as const, label: "History", icon: History },
            ]).map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  "flex items-center gap-1.5 pb-2 text-sm font-medium border-b-2 transition-colors",
                  activeTab === tab.value
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {editingOrder && activeTab === "history" && (
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <OrderHistory order={editingOrder} />
          </div>
        )}

        {(!editingOrder || activeTab === "details") && (
        <form onSubmit={handleFormSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Details: Order Type, Billing Party, Trip Type, Status (edit only) */}
          <FormSection title="Details">
          <div
            className={cn(
              "grid gap-4",
              (() => {
                const showTripType = !isPerLegTripType(formData);
                if (editingOrder) return showTripType ? "sm:grid-cols-4" : "sm:grid-cols-3";
                return showTripType ? "sm:grid-cols-3" : "sm:grid-cols-2";
              })()
            )}
          >
            {!editingOrder && (
              <div>
                <Label>Order Type</Label>
                <Select
                  value={formData.order_type}
                  onValueChange={(v) => handleOrderTypeChange(v as "single-leg" | "multi-leg")}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single-leg">Single-leg</SelectItem>
                    <SelectItem value="multi-leg">Multi-leg</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Billing Party *</Label>
              <Select
                value={formData.billing_party_id}
                onValueChange={(value) => setFormData((prev) => {
                  const next = { ...prev, billing_party_id: value };
                  const c = customers?.find((x) => x.id === value);
                  if (c && !c.authorized_nvo && prev.trip_type === "NVO") {
                    next.trip_type = "";
                  }
                  return next;
                })}
                required
                disabled={!!editingOrder || !!lockBillingPartyId}
              >
                <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.organization_name} ({c.short_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isPerLegTripType(formData) && (
            <div>
              <Label>Trip Type</Label>
              {(() => {
                const selectedCustomer = customers?.find((c) => c.id === formData.billing_party_id);
                const nvoBlocked = !!selectedCustomer && !selectedCustomer.authorized_nvo;
                return (
                  <Select
                    value={formData.trip_type || ""}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, trip_type: value }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select trip type" /></SelectTrigger>
                    <SelectContent>
                      {tripTypeOptions.map((tt) => {
                        const disabled = tt === "NVO" && nvoBlocked;
                        return (
                          <SelectItem key={tt} value={tt} disabled={disabled}>
                            <div className="flex flex-col">
                              <span>{tt}</span>
                              {disabled && (
                                <span className="text-xs text-muted-foreground">
                                  Billing Party is not authorized for NVO
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                );
              })()}
            </div>
            )}
            {editingOrder && (
              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value as OrderStatus }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>{ORDER_STATUS_LABELS[status]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          </FormSection>

          {/* Equipment: trailer, trailer type, tender + advanced equipment state */}
          <FormSection title="Equipment">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {!templateEditMode && !isPerLegAssets(formData) && (
            <div>
              <Label htmlFor="trailer-id">Trailer ID</Label>
              <TrailerIdField
                id="trailer-id"
                value={formData.trailer_id}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, trailer_id: value }));
                }}
                onNeedsServiceBlock={(info) => setNeedsServiceBlock(info)}
              />
            </div>
            )}
            {!isPerLegAssets(formData) && (
            <div>
              <Label>Trailer Type{trailerTypeRequired ? " *" : ""}</Label>
              <Select
                value={formData.trailer_type_id || "none"}
                onValueChange={handleTrailerTypeChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {trailerTypes?.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {matched && inheritedTypeLabel && !trailerTypeOverride && (
                <p className="mt-1 text-xs text-muted-foreground">
                  From checked-in trailer
                </p>
              )}
              {trailerTypeOverride && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Check-in record will be updated on save
                </p>
              )}
            </div>
            )}
            {!templateEditMode && (
            <div>
              <Label htmlFor="tender-id">Tender ID</Label>
              <Input
                id="tender-id"
                value={formData.tender_id}
                onChange={(e) => setFormData((prev) => ({ ...prev, tender_id: e.target.value }))}
                placeholder="e.g., TND-001"
              />
            </div>
            )}
          </div>
          {!templateEditMode && (
            <PendingBolCheckbox
              formData={formData}
              onChange={(next) => setFormData((prev) => ({ ...prev, pending_bol: next }))}
            />
          )}
          {showAdvanced && formData.order_type === "single-leg" && (
            <EquipmentAdvancedFields
              idPrefix="order"
              loadStatus={formData.load_status ?? ""}
              isTemperatureControlled={formData.is_temperature_controlled === true}
              temperatureF={formData.temperature_f ?? ""}
              onLoadStatusChange={(v) => setFormData((prev) => ({ ...prev, load_status: v }))}
              onTemperatureControlledChange={(v) =>
                setFormData((prev) => ({ ...prev, is_temperature_controlled: v }))
              }
              onTemperatureChange={(v) => setFormData((prev) => ({ ...prev, temperature_f: v }))}
            />
          )}
          </FormSection>

          {/* Per-leg asset toggle (multi-leg only). When checked (default), the
              order-level Tractor/Operator pickers below apply to every leg.
              Per-leg pickers will be exposed in a follow-up; for now leaving
              this unchecked still routes the order-level values to legs. */}
          {formData.order_type === "multi-leg" && (() => {
            const sameAssets = formData.use_same_assets_for_all_legs !== false;
            const sameTrip = formData.use_same_trip_type_for_all_legs !== false;
            return (
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="flex items-start gap-2 rounded-lg border border-border bg-accent p-3">
                  <Checkbox
                    id="use_same_assets_for_all_legs"
                    checked={sameAssets}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        use_same_assets_for_all_legs: checked === true,
                      }))
                    }
                  />
                  <div className="grid gap-0.5">
                    <Label
                      htmlFor="use_same_assets_for_all_legs"
                      className="cursor-pointer font-medium"
                    >
                      Use the same tractor, trailer, and operator for all legs.
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {sameAssets
                        ? "Uncheck this option to assign a different tractor, trailer, or operator to each leg."
                        : "Check this option to assign the same tractor, trailer, and operator to all legs."}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-border bg-accent p-3">
                  <Checkbox
                    id="use_same_trip_type_for_all_legs"
                    checked={sameTrip}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        use_same_trip_type_for_all_legs: checked === true,
                      }))
                    }
                  />
                  <div className="grid gap-0.5">
                    <Label
                      htmlFor="use_same_trip_type_for_all_legs"
                      className="cursor-pointer font-medium"
                    >
                      Use the same trip type for all legs.
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {sameTrip
                        ? "Uncheck this option to assign a different trip type to each leg."
                        : "Check this option to assign the same trip type to all legs."}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Stops Section — shared component */}
          <FormSection title="Itinerary">
          {durationWarnings.length > 0 && (
            <div
              role="alert"
              className="mb-3 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="space-y-0.5">
                {durationWarnings.map((w) => (
                  <p key={w.legNumber ?? "order"}>{formatDurationWarning(w)}</p>
                ))}
              </div>
            </div>
          )}
          <OrderStopsSection
            formData={formData}
            setFormData={setFormData}
            locations={locations}
            allowAddStops={true}
            tractors={tractors}
            disabledTractorIds={mergedDisabledTractorIds}
            disabledTractorIdsByLeg={disabledTractorIdsByLeg}
            tractorOosStatusById={oosStatusById}
            tractorOosRecordsById={oosRecordsById}
            drivers={drivers}
            activeTenant={activeTenant}
            nowFloor={nowFloor}
            tractorEligibilityByTractor={eligibilityByTractor}
            tractorEligibilityGateState={eligibilityGateState}
            onTrailerNeedsServiceBlock={(info) => setNeedsServiceBlock(info)}
            showAdvancedFields={showAdvanced}
          />
          </FormSection>

          {/* Assignments — placed below stops so users set the time window first */}
          {!templateEditMode && !isPerLegAssets(formData) && (
          <FormSection title="Assignments">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label>Assigned Tractor</Label>
              <TractorCombobox
                tractors={tractors}
                value={formData.assigned_tractor_id}
                disabledIds={mergedDisabledTractorIds}
                eligibilityReasons={eligibilityReasonsById}
                eligibilityGateState={eligibilityGateState}
                oosStatusById={oosStatusById}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    assigned_tractor_id: value === "none" ? "" : value,
                  }))
                }
              />
            </div>
            <div>
              <Label>Primary Operator</Label>
              <DriverCombobox
                drivers={drivers}
                value={formData.assigned_operator_id}
                onChange={(value) => {
                  const newVal = value === "none" ? "" : value;
                  setFormData((prev) => ({
                    ...prev,
                    assigned_operator_id: newVal,
                    ...(newVal ? {} : { assigned_operator_2_id: "" }),
                  }));
                }}
              />
            </div>
            <div>
              <Label>Secondary Operator</Label>
              <DriverCombobox
                drivers={drivers}
                value={formData.assigned_operator_2_id}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    assigned_operator_2_id: value === "none" ? "" : value,
                  }))
                }
                disabled={!formData.assigned_operator_id || formData.assigned_operator_id === "none"}
              />
            </div>
          </div>
          </FormSection>
          )}

          {/* Shipment (advanced) */}
          {showAdvanced && (
            <FormSection
              title="Shipment"
              description="Matches the shipment fields customers send through the Orders API."
            >
              <ShipmentFields
                freightWeightLbs={formData.freight_weight_lbs ?? ""}
                commodityCode={formData.commodity_code ?? ""}
                commodityDescription={formData.commodity_description ?? ""}
                onFreightWeightChange={(v) =>
                  setFormData((prev) => ({ ...prev, freight_weight_lbs: v }))
                }
                onCommodityCodeChange={(v) =>
                  setFormData((prev) => ({ ...prev, commodity_code: v }))
                }
                onCommodityDescriptionChange={(v) =>
                  setFormData((prev) => ({ ...prev, commodity_description: v }))
                }
              />
            </FormSection>
          )}

          {/* Frequency (recurrence details) — placed at the bottom of the form. */}
          {(!editingOrder || templateEditMode) && formData.is_recurring && (
            <div className="rounded-lg border bg-muted/40 p-4">
              <div className="flex items-center gap-2 mb-3 text-sm font-medium">
                <Repeat className="h-4 w-4" /> Frequency
              </div>
              <div className="space-y-3 mt-2">
                  <div>
                    <Label className="text-sm">Repeat on days</Label>
                    <div className="flex gap-2 mt-1">
                      {DAYS_OF_WEEK.map((day, index) => (
                        <Button
                          key={day}
                          type="button"
                          variant={formData.recurrence_days.includes(index) ? "default" : "outline"}
                          size="sm"
                          className="w-10 h-10 p-0"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              recurrence_days: prev.recurrence_days.includes(index)
                                ? prev.recurrence_days.filter(d => d !== index)
                                : [...prev.recurrence_days, index],
                            }));
                          }}
                        >
                          {day}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <RadioGroup
                      value={recurrenceEndType}
                      onValueChange={(val: "never" | "ends_on") => {
                        setRecurrenceEndType(val);
                        if (val === "never") {
                          setFormData((prev) => ({ ...prev, recurrence_end_date: "" }));
                        }
                      }}
                      className="flex items-center gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="never" id="recurrence_never" />
                        <Label htmlFor="recurrence_never" className="font-normal">Doesn't end</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="ends_on" id="recurrence_ends_on" />
                        <Label htmlFor="recurrence_ends_on" className="font-normal">Ends on</Label>
                        {recurrenceEndType === "ends_on" && (
                          <Input
                            id="recurrence_end_date"
                            type="date"
                            className="w-auto"
                            value={formData.recurrence_end_date}
                            onChange={(e) => setFormData((prev) => ({ ...prev, recurrence_end_date: e.target.value }))}
                          />
                        )}
                      </div>
                    </RadioGroup>
                  </div>
              </div>
            </div>
          )}

          </div>

          <div className="sticky bottom-0 z-10 flex flex-col gap-3 border-t bg-background px-6 py-4">
            <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="outline" onClick={onReset}>Cancel</Button>

            <SubmitWithReasons
              reasons={formValidation.reasons}
              pending={isSubmitting}
            >
              {templateEditMode
                ? "Update Template"
                : forceRecurring && !editingOrder
                ? "Create Template"
                : formData.is_recurring && !editingOrder
                  ? "Create Template"
                  : formData.order_type === "multi-leg" && !editingOrder
                    ? "Create Multi-leg Order"
                  : editingOrder
                    ? "Update Order"
                    : "Create Order"}
            </SubmitWithReasons>
            </div>
          </div>

        </form>
        )}
      </DialogContent>
    </Dialog>
    <NeedsServiceBlockedDialog
      open={needsServiceBlock !== null}
      onOpenChange={(o) => { if (!o) setNeedsServiceBlock(null); }}
      trailerId={needsServiceBlock?.trailerId ?? ""}
      reason={needsServiceBlock?.reason ?? null}
    />
    <CheckedInTrailerTypeOverrideDialog
      open={pendingTypeChange !== null}
      onConfirm={confirmTrailerTypeOverride}
      onCancel={cancelTrailerTypeOverride}
    />
    </>
  );
}
